import { useState } from 'react'

import _ from 'lodash'
import { toast } from 'sonner'

import { useDigitalProductStore } from '@/features/store/hook'
import { ticketDividerBorderClass } from '@/features/store/lib/borderClasses'
import { DigitalProductStoreRefundConfirmModal } from '@/features/store/Refund/Modals/Confirm'
import { DigitalProductStoreWithdrawConfirmModal } from '@/features/store/Withdraw/Modals/Confirm'
import { useModalContext } from '@/shared/hooks'
import {
  HOLD_TO_CONFIRM_MS,
  holdSurfaceClassName,
  useHoldToConfirm
} from '@/shared/hooks/useHoldToConfirm'

import { DigitalProductStoreBottomSheetRewardRequestItemLabel } from './ItemLabel'
import { requestTimestamps } from '../../../lib/requestTimestamps'

const REWARD_IMAGE_PLACEHOLDER = '/placeholders/reward.jpg'

// `setTimeout` acima de 2³¹−1 ms transborda e dispara na hora. Uma janela
// configurada com semanas de antecedência produz exatamente esse atraso.
export const DigitalProductStoreBottomSheetRewardRequest = ({
  id,
  reward_item,
  status,
  quantity,
  created_at,
  delivered_at,
  onWithdraw
}) => {
  // Reserva e retirada, formatados. O operador lê a reserva no balcão para
  // saber se a solicitação é de agora ou de horas atrás; a retirada fecha o
  // histórico. Solicitação antiga vem sem retirada e fica sem.
  const { reservedAt, deliveredAt } = requestTimestamps({ created_at, delivered_at })
  const [transactioning, setTransactioning] = useState({
    on: false,
    kind: null
  })
  const { transaction } = useDigitalProductStore()
  const { showModal } = useModalContext()

  /* Quem diz que a reserva expirou é a api, e só ela.
   *
   * Antes esta tela derivava o vencimento do relógio local: um `approved` que
   * passasse de `expires_at` exibia "Expirado". Desde que a expiração
   * automática foi desligada (2026-09-17), a reserva **não** expira sozinha —
   * as moedas seguem retidas até a retirada ou até o Produtor encerrar as
   * pendentes. Derivar aqui faria a tela anunciar uma devolução que não
   * aconteceu, com o item ainda reservado no servidor.
   */
  const awaitingWithdrawal = status === 'approved'
  const isExpired = status === 'expired'

  const bgClass = awaitingWithdrawal
    ? 'bg-warning-light'
    : status === 'delivered'
    ? 'bg-green-500'
    : status === 'canceled'
    ? 'bg-red-100'
    : 'bg-gray-300'
  const textClass = status === 'delivered' ? 'text-white' : 'text-gray-900'
  // Borda scalloped do ticket (--ticket-border-color) deve casar exatamente com
  // o bg-* de cada status: usa os mesmos tokens dos utilitários (fallback hex
  // para o valor padrão do token).
  const borderColor = awaitingWithdrawal
    ? 'var(--color-warning-light, #fff4de)'
    : status === 'delivered'
    ? 'var(--color-green-500, #22c55e)'
    : status === 'canceled'
    ? 'var(--color-red-100, #fee2e2)'
    : 'var(--color-gray-300, #e1e3ea)'
  const dividerBorderClass = ticketDividerBorderClass(
    isExpired ? 'expired' : status
  )
  // A auto-retirada é da variação: foi ela que dispensou a lojinha.
  const autoRedeemed = Boolean(reward_item?.auto_redeem)
  const imageUrl = _.get(
    reward_item,
    'image.file.url',
    REWARD_IMAGE_PLACEHOLDER
  )

  const handleNoty = () => {
    if (status === 'delivered') {
      toast.success('Retirada realizada', {
        description: 'Esperamos que tenha gostado do item!'
      })
    }
  }

  const finalizeTransaction = () => setTransactioning({ on: false, kind: null })

  const handleTransaction = kind => {
    if (!_.includes(['withdraw', 'refund'], kind)) return

    setTransactioning({ on: true, kind })
    showModal({
      component:
        kind === 'withdraw'
          ? DigitalProductStoreWithdrawConfirmModal
          : DigitalProductStoreRefundConfirmModal,
      onHide: finalizeTransaction,
      onConfirm: async () => {
        await transaction({ ids: [id], kind })
        onWithdraw()
        setTimeout(() => {
          finalizeTransaction()
        }, 800)
      },
      onCancel: finalizeTransaction
    })
  }

  // O gesto é do ticket inteiro — a mesma superfície que o arrasto ocupava.
  const holdEnabled = awaitingWithdrawal && !transactioning.on
  const { isHolding, holdProps } = useHoldToConfirm({
    onConfirm: () => handleTransaction('withdraw'),
    enabled: holdEnabled,
    label: 'Segure para confirmar a retirada'
  })

  if (!_.includes(['approved', 'delivered', 'canceled', 'expired'], status))
    return null

  return (
    <div
      className={`reward-request ticket relative mx-auto w-full rounded p-3 px-5 ${bgClass} ${textClass} ${
        awaitingWithdrawal ? `animate-ticket-pulse ${holdSurfaceClassName}` : ''
      }`}
      style={{ '--ticket-border-color': borderColor }}
      {...(holdEnabled ? holdProps : { onClick: handleNoty })}
    >
      {/* A barra é o cronômetro, e o único aviso de progresso que chega a todo
          mundo — a vibração não existe no iOS. Verde porque é a cor do estado
          que o gesto produz: o ticket se enche do que vai virar.

          O recorte vive num wrapper interno, e não no ticket: a borda
          serrilhada é desenhada em `::before`/`::after` fora da caixa, e
          `overflow-hidden` no ticket cortaria justamente ela.

          Escala em vez de largura: roda no compositor e não repinta a cada
          quadro, que é o que salva a animação em aparelho fraco. */}
      {holdEnabled ? (
        <span
          className="pointer-events-none absolute inset-0 overflow-hidden rounded"
          aria-hidden="true">
          <span
            className={`block h-full w-full origin-left bg-green-500/40 transition-transform ease-linear ${
              isHolding ? 'scale-x-100' : 'scale-x-0'
            }`}
            style={{
              transitionDuration: isHolding ? `${HOLD_TO_CONFIRM_MS}ms` : '160ms'
            }}
          />
        </span>
      ) : null}

      <div className="relative flex items-center justify-between">
        {/* Quem entrega reconhece o brinde de relance, sem ler o rótulo. */}
        <img
          src={imageUrl}
          alt=""
          className="mr-3 h-10 w-10 shrink-0 rounded object-cover"
        />
        <DigitalProductStoreBottomSheetRewardRequestItemLabel
          quantity={quantity}
          rewardItem={reward_item}
        />
        <div
          className={`mx-3 h-[40px] w-px border border-dashed ${dividerBorderClass}`}
        />
        <div className="text-right">
          {status === 'delivered' ? (
            <div className="flex w-[110px] flex-col items-end justify-end gap-0.5">
              <span className="block text-right text-xs font-bold">
                Resgatado e {autoRedeemed ? 'Auto-' : ''}Retirado
              </span>
              {deliveredAt && (
                <span className="block text-right text-2xs opacity-90">
                  Retirado {deliveredAt}
                </span>
              )}
              {reservedAt && (
                <span className="block text-right text-2xs opacity-75">
                  Reservado {reservedAt}
                </span>
              )}
            </div>
          ) : status === 'canceled' ? (
            <div className="flex w-[100px] flex-nowrap items-center justify-end gap-3">
              <span className="block text-right text-xs font-bold">
                Reembolsado
              </span>
            </div>
          ) : isExpired ? (
            <div className="flex w-[100px] flex-nowrap items-center justify-end gap-3">
              <span className="block text-right text-xs font-bold text-gray-500">
                Expirado
              </span>
            </div>
          ) : (
            <div className="flex w-[110px] flex-col items-end justify-end gap-1">
              {reservedAt && (
                <span className="text-2xs text-right">Reservado {reservedAt}</span>
              )}
              <span className="text-2xs">
                {isHolding ? 'segure...' : 'segure para retirar'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* O reembolso é a saída, não o caminho: sem ênfase, embaixo, e some
          depois da retirada porque retirado é estado final. `data-no-hold`
          impede que tocá-lo inicie a confirmação da retirada. */}
      {awaitingWithdrawal ? (
        <div
          className={`relative mt-3 border-t border-dashed pt-2 text-center ${dividerBorderClass}`}>
          <button
            data-no-hold
            type="button"
            disabled={transactioning.on}
            onClick={() => handleTransaction('refund')}
            className="text-2xs font-semibold text-gray-600 underline underline-offset-2 disabled:opacity-50">
            Reembolsar
          </button>
        </div>
      ) : null}
    </div>
  )
}
